
import { distinct } from '@/util/array-util';
import Vue from 'vue';

export interface Column {
  header: string;
  value: string;
  type: 'string' | 'number';
  sort?: 'asc' | 'desc';
}

interface SortBy {
  column: Column;
  order: 'asc' | 'desc';
}
interface FilterBy {
  column: Column;
  values: string[];
}
export default Vue.extend({
  props: {
    items: { type: Array as () => any[], required: true },
    columns: { type: Array as () => Column[], required: true },
  },

  data() {
    return {
      filterBy: this.columns.map((column) => ({ column, values: [] })) as FilterBy[],
      sortBy: this.columns.filter((column) => column.sort).map((column) => ({ column, order: column.sort })) as SortBy[],
    };
  },

  computed: {
    sorted(): any[] {
      const sorted = [...this.items].sort(this.compareFunction(this.sortBy));
      return this.filterItems(sorted, this.filterBy);
    },
  },

  methods: {
    getValue(item: any, column: Column): string {
      const path = column.value.split('.');
      let value = item;
      for (const p of path) {
        if (value === undefined) {
          return '';
        }
        value = value[p];
      }
      return value;
    },
    getValues(column: Column): any[] {
      const values = this.items.map((item) => this.getValue(item, column));
      return distinct(values)
        .sort((a, b) => a.localeCompare(b));
    },
    compare(a: any, b: any, column: Column): number {
      const v1 = this.getValue(a, column);
      const v2 = this.getValue(b, column);
      // if string
      if (typeof v1 === 'string' && typeof v2 === 'string') {
        return v1.localeCompare(v2);
      } else if (v1 < v2) {
        return -1;
      } else if (v1 > v2) {
        return 1;
      } else {
        return 0;
      }
    },

    updateSortBy(column: Column) {
      const index = this.sortBy.findIndex((s) => s.column.value === column.value);
      if (index === 0) {
        this.sortBy[0].order = this.sortBy[0].order === 'asc' ? 'desc' : 'asc';
      } else if (index > 0) {
        // move to front
        this.sortBy.splice(0, 0, this.sortBy.splice(index, 1)[0]);
      } else {
        this.sortBy.unshift({ column, order: 'asc' });
      }
    },
    sortByIndex(column: Column): number | undefined {
      const i = this.sortBy.findIndex((s) => s.column.value === column.value);
      if (i >= 0) {
        return i + 1;
      } else {
        return undefined;
      }
    },
    updateFilter(column: Column, value: string) {
      const filter = this.filterBy.find((f) => f.column.value === column.value);
      // add or remove value
      if (filter) {
        if (filter.values.includes(value)) {
          filter.values.splice(filter.values.indexOf(value), 1);
        } else {
          filter.values.push(value);
        }
      }
    },
    isFiltered(column: Column, value: string): string | undefined {
      const filter = this.filterBy.find((f) => f.column.value === column.value);
      return filter?.values.find((v) => v === value);
    },
    round(a: number): number {
      return Math.round(a * 1000) / 1000;
    },
    compareFunction(sortBy: SortBy[]) {
      return (a, b) => {
        for (const sort of sortBy) {
          const compareResult = this.compare(a, b, sort.column);
          if (compareResult !== 0) {
            return sort.order === 'asc' ? compareResult : -compareResult;
          }
        }
        return 0;
      };
    },
    filterItems(items: any[], filterBy: FilterBy[]): any[] {
      return items.filter((item) => {
        for (const filter of filterBy) {
          if (filter.values.length > 0 && !filter.values.includes(this.getValue(item, filter.column))) {
            return false;
          }
        }
        return true;
      });
    },
    filterActive(column: Column): boolean {
      return this.filterBy.find((f) => f.column.value === column.value)?.values.length === 0;
    },
  },
});
